import React, { lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import DashboardLayout from './layouts/DashboardLayout';
import Login from './pages/Login';
import CategoryHub from './pages/CategoryHub';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const ActivityLog = lazy(() => import('./pages/ActivityLog'));
const Team = lazy(() => import('./pages/Team'));
const Drivers = lazy(() => import('./pages/Drivers'));
const Vehicles = lazy(() => import('./pages/Vehicles'));
const VehicleBookingRequests = lazy(() => import('./pages/VehicleBookingRequests'));
const Rides = lazy(() => import('./pages/Rides'));
const Users = lazy(() => import('./pages/Users'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Services = lazy(() => import('./pages/Services'));
const Settings = lazy(() => import('./pages/Settings'));
const Tickets = lazy(() => import('./pages/Tickets'));
const DriverNotices = lazy(() => import('./pages/DriverNotices'));
const QuickChatConversations = lazy(() => import('./pages/QuickChatConversations'));
const Wallet = lazy(() => import('./pages/Wallet'));
const Withdrawals = lazy(() => import('./pages/Withdrawals'));
const Ratings = lazy(() => import('./pages/Ratings'));
const ServiceAreas = lazy(() => import('./pages/ServiceAreas'));
const CommuteRouteDetail = lazy(() => import('./pages/CommuteRouteDetail'));
const CommuteTripDetail = lazy(() => import('./pages/CommuteTripDetail'));
const SystemNotifications = lazy(() => import('./pages/SystemNotifications'));
const CustomNotifications = lazy(() => import('./pages/CustomNotifications'));
const Referrals = lazy(() => import('./pages/Referrals'));
const ReferralSettings = lazy(() => import('./pages/ReferralSettings'));
const Coupons = lazy(() => import('./pages/Coupons'));
const CancellationStats = lazy(() => import('./pages/CancellationStats'));
const CommuteBookings = lazy(() => import('./pages/CommuteBookings'));
const CommutePasses = lazy(() => import('./pages/CommutePasses'));
const CommuteRoutesPage = lazy(() => import('./pages/CommuteRoutesPage'));
const CommuteTripsPage = lazy(() => import('./pages/CommuteTripsPage'));
const CommuteWaitlistPage = lazy(() => import('./pages/CommuteWaitlistPage'));
const RequestsDemand = lazy(() => import('./pages/RequestsDemand'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const Analytics = lazy(() => import('./pages/Analytics'));
const WebsiteLeads = lazy(() => import('./pages/WebsiteLeads'));
const MarketingDashboard = lazy(() => import('./pages/MarketingDashboard'));
const VehicleCategories = lazy(() => import('./pages/VehicleCategories'));
const RentalPackages = lazy(() => import('./pages/RentalPackages'));
const RentalDisputes = lazy(() => import('./pages/RentalDisputes'));
const RentalDynamicCharges = lazy(() => import('./pages/RentalDynamicCharges'));
const ScheduledRides = lazy(() => import('./pages/ScheduledRides'));
const OutstationSettings = lazy(() => import('./pages/OutstationSettings'));
const OutstationBookings = lazy(() => import('./pages/OutstationBookings'));
const WeddingSettings = lazy(() => import('./pages/WeddingSettings'));
const WeddingBookings = lazy(() => import('./pages/WeddingBookings'));
const WeddingBookingDetail = lazy(() => import('./pages/WeddingBookingDetail'));
const WeddingAvailability = lazy(() => import('./pages/WeddingAvailability'));
const WeddingVehiclePricing = lazy(() => import('./pages/WeddingVehiclePricing'));
const CoachListings = lazy(() => import('./pages/coach/CoachListings'));
const CoachSettings = lazy(() => import('./pages/coach/CoachSettings'));
const CoachBookings = lazy(() => import('./pages/coach/CoachBookings'));
const CoachBookingDetail = lazy(() => import('./pages/coach/CoachBookingDetail'));
const CoachOperations = lazy(() => import('./pages/coach/CoachOperations'));
const OutstationBookingDetail = lazy(() => import('./pages/OutstationBookingDetail'));
const RideDetail = lazy(() => import('./pages/RideDetail'));
const RentalRideDetail = lazy(() => import('./pages/RentalRideDetail'));
const CommuteSalaryConfig = lazy(() => import('./pages/CommuteSalaryConfig'));
const CommuteIncentiveRules = lazy(() => import('./pages/CommuteIncentiveRules'));
const CommuteAttendance = lazy(() => import('./pages/CommuteAttendance'));
const CommutePenalties = lazy(() => import('./pages/CommutePenalties'));
const CommutePayoutBatches = lazy(() => import('./pages/CommutePayoutBatches'));
const CommuteDriverLedger = lazy(() => import('./pages/CommuteDriverLedger'));
const TermsEditor = lazy(() => import('./pages/TermsEditor'));
const PromoBanners = lazy(() => import('./pages/PromoBanners'));
const RentalRules = lazy(() => import('./pages/RentalRules'));
const CoachRates = lazy(() => import('./pages/CoachRates'));
const BillingBills = lazy(() => import('./pages/billing/BillingBills'));
const BillingEditor = lazy(() => import('./pages/billing/BillingEditor'));
const BillingBillDetail = lazy(() => import('./pages/billing/BillingBillDetail'));
const BillingCustomers = lazy(() => import('./pages/billing/BillingCustomers'));
const BillingItems = lazy(() => import('./pages/billing/BillingItems'));
const BillingSettings = lazy(() => import('./pages/billing/BillingSettings'));

import { SocketProvider } from './context/SocketContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { canOpenPath, homePathFor } from './config/adminAccess';
import { CategoryProvider } from './context/CategoryContext';
import { GoogleMapsProvider } from './context/GoogleMapsContext';
import AdminNotificationListener from './components/AdminNotificationListener';
import { NotifierProvider } from './components/common/Notifier';

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated, roles } = useAuth();
    const { pathname } = useLocation();
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    // Marketing and operations logins only get their own pages; any other page sends them home.
    if (!canOpenPath(roles, pathname)) return <Navigate to={homePathFor(roles)} replace />;
    return children;
};

function App() {
    return (
        <AuthProvider>
            <NotifierProvider>
            <CategoryProvider>
                <SocketProvider>
                    <GoogleMapsProvider>
                    <Router>
                        <AdminNotificationListener />
                        <Routes>
                            <Route path="/login" element={<Login />} />

                            {/* Category Hub — landing page after login */}
                            <Route path="/categories" element={
                                <PrivateRoute>
                                    <CategoryHub />
                                </PrivateRoute>
                            } />

                            <Route path="/" element={
                                <PrivateRoute>
                                    <DashboardLayout />
                                </PrivateRoute>
                            }>
                                <Route index element={<Dashboard />} />
                                <Route path="drivers" element={<Drivers />} />
                                <Route path="vehicles" element={<Vehicles />} />
                                <Route path="vehicle-booking-requests" element={<VehicleBookingRequests />} />
                                <Route path="vehicle-categories" element={<VehicleCategories />} />
                                <Route path="promo-banners" element={<PromoBanners />} />
                                <Route path="rental-packages" element={<RentalPackages />} />
                                <Route path="rental-rules" element={<RentalRules />} />
                                <Route path="coach-rates" element={<CoachRates />} />
                                <Route path="rental-surcharges" element={<RentalDynamicCharges />} />
                                <Route path="rental-disputes" element={<RentalDisputes />} />
                                <Route path="rides" element={<Rides />} />
                                <Route path="rides/:rideId" element={<RideDetail />} />
                                <Route path="rental-rides/:rideId" element={<RentalRideDetail />} />
                                <Route path="scheduled-rides" element={<ScheduledRides />} />
                                <Route path="users" element={<Users />} />
                                <Route path="transactions" element={<Transactions />} />
                                <Route path="wallet" element={<Wallet />} />
                                <Route path="withdrawals" element={<Withdrawals />} />
                                <Route path="services" element={<Services />} />
                                <Route path="settings" element={<Settings />} />
                                <Route path="terms-editor" element={<TermsEditor />} />
                                <Route path="tickets" element={<Tickets />} />
                                <Route path="driver-notices" element={<DriverNotices />} />
                                <Route path="quick-chat-support" element={<Navigate to="/quick-chat-conversations" replace />} />
                                <Route path="quick-chat-conversations" element={<QuickChatConversations />} />
                                <Route path="ratings" element={<Ratings />} />
                                <Route path="geofencing" element={<ServiceAreas />} />
                                <Route path="commute-routes" element={<CommuteRoutesPage />} />
                                <Route path="commute-trips" element={<CommuteTripsPage />} />
                                <Route path="commute-waitlist" element={<CommuteWaitlistPage />} />
                                <Route path="requests-demand" element={<RequestsDemand />} />
                                <Route path="commute/routes/:routeId" element={<CommuteRouteDetail />} />
                                <Route path="commute/trips/:tripId" element={<CommuteTripDetail />} />
                                <Route path="notifications" element={<SystemNotifications />} />
                                <Route path="send-notification" element={<CustomNotifications />} />
                                <Route path="referrals" element={<Referrals />} />
                                <Route path="referral-settings" element={<ReferralSettings />} />
                                <Route path="coupons" element={<Coupons />} />
                                <Route path="cancellations" element={<CancellationStats />} />
                                <Route path="commute-bookings" element={<CommuteBookings />} />
                                <Route path="commute-passes" element={<CommutePasses />} />
                                <Route path="maintenance" element={<Maintenance />} />
                                <Route path="activity-log" element={<ActivityLog />} />
                                <Route path="team" element={<Team />} />
                                <Route path="analytics" element={<Analytics />} />
                                <Route path="website-leads" element={<WebsiteLeads />} />
                                <Route path="marketing" element={<MarketingDashboard />} />
                                <Route path="outstation-bookings" element={<OutstationBookings />} />
                                <Route path="outstation-bookings/:id" element={<OutstationBookingDetail />} />
                                <Route path="outstation-settings" element={<OutstationSettings />} />
                                <Route path="outstation-fares" element={<Navigate to="/outstation-settings" replace />} />
                                <Route path="outstation-rides" element={<Navigate to="/outstation-bookings" replace />} />
                                <Route path="outstation-charges" element={<Navigate to="/outstation-bookings" replace />} />
                                <Route path="wedding-bookings" element={<WeddingBookings />} />
                                <Route path="wedding-bookings/:id" element={<WeddingBookingDetail />} />
                                <Route path="wedding-availability" element={<WeddingAvailability />} />
                                <Route path="wedding-pricing" element={<WeddingVehiclePricing />} />
                                <Route path="wedding-settings" element={<WeddingSettings />} />
                                <Route path="coach-listings" element={<CoachListings />} />
                                <Route path="coach-settings" element={<CoachSettings />} />
                                <Route path="coach-bookings" element={<CoachBookings />} />
                                <Route path="coach-bookings/:id" element={<CoachBookingDetail />} />
                                <Route path="coach-operations" element={<CoachOperations />} />
                                <Route path="commute-salary-config" element={<CommuteSalaryConfig />} />
                                <Route path="commute-incentive-rules" element={<CommuteIncentiveRules />} />
                                <Route path="commute-attendance" element={<CommuteAttendance />} />
                                <Route path="commute-penalties" element={<CommutePenalties />} />
                                <Route path="commute-payout-batches" element={<CommutePayoutBatches />} />
                                <Route path="commute-driver-ledger" element={<CommuteDriverLedger />} />
                                <Route path="bills" element={<BillingBills />} />
                                <Route path="bills/new" element={<BillingEditor key="new" />} />
                                <Route path="bills/:id/edit" element={<BillingEditor />} />
                                <Route path="bills/:id" element={<BillingBillDetail />} />
                                <Route path="billing-customers" element={<BillingCustomers />} />
                                <Route path="billing-items" element={<BillingItems />} />
                                <Route path="billing-settings" element={<BillingSettings />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/categories" replace />} />
                        </Routes>
                    </Router>
                    </GoogleMapsProvider>
                </SocketProvider>
            </CategoryProvider>
            </NotifierProvider>
        </AuthProvider>
    );
}

export default App;
